
import { ExclamationTriangleIcon, CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/solid';

interface ErrorBannerProps {
  title: string;
  message?: string;
  action?: {
    text: string;
    onClick: () => void;
  };
  type?: 'error' | 'warning' | 'success';
  dismissible?: boolean;
  onDismiss?: () => void;
}

const styles = {
  error: {
    container: 'bg-red-50 border-red-200 text-red-800',
    icon: 'text-red-500',
    button: 'text-red-800 hover:text-red-900',
  },
  warning: {
    container: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    icon: 'text-yellow-500',
    button: 'text-yellow-800 hover:text-yellow-900',
  },
  success: {
    container: 'bg-green-50 border-green-200 text-green-800',
    icon: 'text-green-500',
    button: 'text-green-800 hover:text-green-900',
  },
};

const ErrorBanner = (props: ErrorBannerProps) => {
  const { title, message, action, type = 'error', dismissible, onDismiss } = props;
  const currentStyle = styles[type];
  return (
    <div
      className={`border rounded-lg p-4 mb-6 transform transition-all duration-300 ease-out animate-in slide-in-from-top-2 fade-in-0 ${currentStyle.container}`}
      role={type === 'error' ? 'alert' : undefined}
      aria-live="polite"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          <div className={`flex-shrink-0 ${currentStyle.icon}`}>
            {type === 'error' && <ExclamationTriangleIcon className="h-5 w-5" />}
            {type === 'warning' && <ExclamationTriangleIcon className="h-5 w-5" />}
            {type === 'success' && <CheckCircleIcon className="h-5 w-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium">{title}</h3>
            {message && <p className="mt-1 text-sm opacity-90">{message}</p>}
            {action && (
              <button
                onClick={action.onClick}
                className={`mt-2 text-sm font-medium underline transition-colors duration-150 ${currentStyle.button}`}
              >
                {action.text}
              </button>
            )}
          </div>
        </div>
        {dismissible && (
          <button
            onClick={onDismiss}
            className={`flex-shrink-0 ml-4 p-1 rounded-md hover:bg-black hover:bg-opacity-10 transition-colors duration-150 ${currentStyle.icon}`}
            aria-label="Dismiss"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ErrorBanner; 